import { yupResolver } from '@hookform/resolvers/yup';
import React, { useEffect, useState } from 'react';
import { Col, Form, Row } from 'react-bootstrap';
import { useFieldArray, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { BiSolidMinusCircle, BiSolidPlusCircle } from 'react-icons/bi';
import { v4 as uuidv4 } from 'uuid';
import * as Yup from 'yup';

import { useAppSelector } from '@/app/hook';
import { PSButton } from '@/components/ui/button';
import { selectUseId } from '@/features/auth/auth.slice';
import { BlockType } from '@/interfaces/models/Knowledge/scenario.interface';

import HintBlock from '../../../../common/HintBlock';
import AddNoteButton from '../../AddNoteButton';
import NoteForm, { getNoteSchema, INoteForm } from '../../NoteForm';
import ScenarioHeader from '../../ScenarioHeader';
import { MacBotActionKind } from '../../types';
import { ScenarioSimpleTextInput } from '../text-scenario/scenario-simple-text-input';
import VideoScenarioDeleteButton from './video-scenario-delete-button';
import ScenarioSide from './video-scenario-side';

interface IFormData extends INoteForm {
  name: string;
  operator: boolean;
  conditions: Condition[];
}

interface Condition {
  variable: string;
  comparisonType: string;
  value?: string;
  isCaseSensitive: boolean;
}

export const MacBotConditionForm = ({
  setShow,
  itemData,
  parentId,
  tree,
  loading,
  createScenarioBlock,
  deleteScenarioBlock,
  changeScenarioBlock,
  config,
}: {
  setShow: (item: boolean) => void;
  itemData: any;
  loading: boolean;
  createScenarioBlock: (block: any) => Promise<any>;
  deleteScenarioBlock: (block: any) => void;
  changeScenarioBlock: (block: any) => Promise<any>;
  parentId: string | null;
  config: any;
  tree: any[];
}) => {
  const { t } = useTranslation();
  const userId = useAppSelector(selectUseId);
  const [isDelete, setIsDelete] = useState(true);

  const comparisonTypes = [
    { key: t('MacBotConditionForm/equals'), value: 'EQUAL' },
    { key: t('MacBotConditionForm/not_equals'), value: 'NOT_EQUAL' },
    { key: t('MacBotConditionForm/contains'), value: 'CONTAINS' },
    { key: t('MacBotConditionForm/not_contains'), value: 'START_WITH' },
    { key: t('MacBotConditionForm/no_value'), value: 'HAS_NO_VALUE' },
    { key: t('MacBotConditionForm/has_value'), value: 'HAS_VALUE' },
    { key: t('MacBotConditionForm/more_than'), value: 'MORE_THAN' },
    {
      key: t('MacBotConditionForm/more_or_equal_than'),
      value: 'MORE_OR_EQUAL_THAN',
    },
    { key: t('MacBotConditionForm/less_than'), value: 'LESS_THAN' },
    {
      key: t('MacBotConditionForm/less_or_equal_than'),
      value: 'LESS_OR_EQUAL_THAN',
    },
  ];

  const numbersComparisonTypes = ['MORE_THAN', 'MORE_OR_EQUAL_THAN', 'LESS_THAN', 'LESS_OR_EQUAL_THAN'];

  const schema = Yup.object({
    name: Yup.string().trim().max(30, t('Wizard/validationLabels/key16')).required(t('MacBotConditionForm/scenario_condition_name_required')),
    operator: Yup.boolean().required(t('MacBotConditionForm/scenario_condition_operator_required')),
    conditions: Yup.array().of(
      Yup.object().shape({
        variable: Yup.string().trim().required(t('MacBotConditionForm/scenario_condition_variable_required')),
        value: Yup.mixed().when('comparisonType', (comparisonType, schema) => {
          const comparisonStr = String(comparisonType);

          if (comparisonStr.includes('HAS_NO_VALUE') || comparisonStr.includes('HAS_VALUE')) {
            return schema.notRequired().optional();
          }

          if (numbersComparisonTypes.includes(comparisonStr)) {
            return Yup.string()
              .required(t('MacBotConditionForm/scenario_condition_value_required'))
              .test('is-valid-number-or-variable', t('MacBotConditionForm/scenario_condition_value_must_be_number'), value => {
                if (typeof value !== 'string') return false;
                if (value.trim().startsWith('$:var_')) return true;
                return !isNaN(Number(value));
              });
          }

          return Yup.string().trim().required(t('MacBotConditionForm/scenario_condition_value_required'));
        }),
        comparisonType: Yup.string().trim().required(t('MacBotConditionForm/scenario_condition_comparison_type_required')),
        isCaseSensitive: Yup.boolean(),
      })
    ),
    ...getNoteSchema(t),
  });

  const {
    register,
    formState: { errors },
    reset,
    handleSubmit,
    control,
    setValue,
    getValues,
    watch,
  } = useForm<IFormData>({
    mode: 'all',
    resolver: yupResolver(schema) as any,
    defaultValues: {
      notes: [
        {
          note_id: uuidv4(),
          creation_date_time: new Date().toISOString(),
          title: '',
          description: '',
          qc: false,
          author: userId,
        },
      ],
    },
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'conditions',
  });
  const { fields: noteFields } = useFieldArray({
    control,
    name: 'notes',
  });
  const [contextMenuList, setContextMenuList] = useState<any[]>([]);

  useEffect(() => {
    if (config?.metaData?.variables) {
      setContextMenuList([...config.metaData.variables]);
    }
  }, [config]);

  const deleteHandler = (e: any) => {
    e?.preventDefault();
    deleteScenarioBlock(itemData);
    setShow(false);
  };

  const onSubmit = (data: any) => {
    data.conditions.forEach((item: any) => {
      let result;
      try {
        result = JSON.parse(item.value);
      } catch (ex) {
        result = item.value;
      }
      item.value = result;
    });

    if (itemData) {
      const _data = {
        ...itemData,
        id: itemData.id,
        title: data.name,
        type: BlockType.Condition,
        parent: itemData.parent,
        ...data,
        operator: data.operator ? 'OR' : 'AND',
      };
      changeScenarioBlock(_data).then(() => {
        reset();
        setShow(false);
      });
    } else {
      const _data = {
        type: BlockType.Condition,
        title: data.name,
        parent: parentId,
        ...data,
        operator: data.operator ? 'OR' : 'AND',
      };
      createScenarioBlock(_data).then(() => {
        reset();
        setShow(false);
      });
    }
  };

  useEffect(() => {
    if (!itemData) {
      setIsDelete(false);
    } else {
      reset({
        name: itemData?.title ?? '',
        operator: itemData?.operator && itemData.operator === 'OR' ? true : false,
        conditions: itemData?.conditions ?? [{ variable: '', comparisonType: '', isCaseSensitive: false }],
        notes: itemData?.notes,
      });
    }
  }, [itemData]);

  return (
    <React.Fragment>
      <ScenarioSide size="md" onSubmit={handleSubmit(onSubmit)}>
        <ScenarioHeader
          type={MacBotActionKind.ShowConditionForm}
          onClose={() => {
            reset();
          }}
        />

        <div className="md:tw-p-6 tw-p-4 tw-bg-gray-50 tw-flex tw-flex-col tw-gap-y-4 tw-w-full">
          <Row>
            <Col xs={12}>
              <Form.Group>
                <Form.Label htmlFor="" className="tw-text-[#687076] tw-text-xs tw-font-normal tw-font-['Lexend'] tw-leading-none">
                  {t('MacBotConditionForm/key10')}
                </Form.Label>
                <Form.Control
                  className="tw-self-stretch tw-px-4 tw-py-3 tw-bg-white tw-rounded-md tw-shadow tw-border tw-border-gray-300"
                  maxLength={31}
                  max={31}
                  type="text"
                  isInvalid={!!errors.name}
                  placeholder={''}
                  {...register('name')}
                />
                <HintBlock texts={['MacBotConditionForm/maxlenght']} />
                <Form.Control.Feedback type="invalid" className="d-block font-poppins">
                  {errors.name?.message}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
          </Row>

          <div className="h-[0px] border border-gray-200"></div>

          <Row>
            <Col xs={12}>
              <Form.Group>
                <div className="tw-flex tw-items-center tw-justify-between">
                  <div className="tw-text-[#687076] tw-text-xs tw-font-normal tw-font-['Lexend'] tw-leading-none">{t('MacBotConditionForm/key2')}</div>

                  <div className="tw-flex tw-gap-4 tw-items-center">
                    <span className="tw-text-zinc-900 tw-text-sm tw-font-normal tw-font-['Lexend'] tw-leading-tight">{t('MacBotConditionForm/key3')}</span>
                    <Form.Switch className="input-switch-blue" {...register('operator')} />
                    <span className="tw-text-zinc-900 tw-text-sm tw-font-normal tw-font-['Lexend'] tw-leading-tight">{t('MacBotConditionForm/key4')}</span>
                  </div>
                </div>
              </Form.Group>
            </Col>
          </Row>

          <div className="h-[0px] border border-gray-200"></div>

          <Row>
            <Col xs={12}>
              {fields.map((field, index) => (
                <div key={field.id}>
                  <div className="tw-flex tw-items-center tw-justify-between">
                    <div className="tw-text-[#687076] tw-text-xs tw-font-normal tw-font-['Lexend'] tw-leading-none">
                      {t('MacBotConditionForm/key1')} {index + 1}
                    </div>
                  </div>
                  <div className="tw-flex tw-justify-between tw-items-center tw-mt-2 mb-4 g-md-3 md:tw-gap-y-0 tw-gap-y-3">
                    <div className="tw-flex-col tw-flex tw-justify-between tw-gap-3 tw-w-full">
                      <Form.Group className="tw-w-full">
                        <Form.Label htmlFor="" className="tw-text-[#687076] tw-text-xs tw-font-normal tw-font-['Lexend'] tw-leading-none">
                          {t('MacBotConditionForm/key5')}
                        </Form.Label>
                        <Form.Select
                          translate="no"
                          className="tw-self-stretch tw-px-4 tw-py-3 tw-bg-white tw-rounded-md tw-shadow tw-border tw-border-gray-300"
                          isInvalid={!!errors.conditions?.[index]?.variable}
                          {...register(`conditions.${index}.variable`)}
                        >
                          <option value={''}>{t('MacBotConditionForm/key6')}</option>

                          {config?.metaData.variables?.map((item: any) => (
                            <option key={item.variableName} value={item.variableName}>
                              {item.name}
                            </option>
                          ))}
                        </Form.Select>
                        <Form.Control.Feedback type="invalid" className="d-block font-poppins">
                          {errors.conditions?.[index]?.variable?.message}
                        </Form.Control.Feedback>
                      </Form.Group>
                      <Form.Group className="tw-w-full">
                        <Form.Label htmlFor="" className="tw-text-[#687076] tw-text-xs tw-font-normal tw-font-['Lexend'] tw-leading-none">
                          {t('MacBotConditionForm/key7')}
                        </Form.Label>
                        <Form.Select
                          translate="no"
                          className="tw-self-stretch tw-px-4 tw-py-3 tw-bg-white tw-rounded-md tw-shadow tw-border tw-border-gray-300"
                          isInvalid={!!errors.conditions?.[index]?.comparisonType}
                          {...register(`conditions.${index}.comparisonType`, {
                            onChange: e => {
                              if (e.target.value === 'HAS_NO_VALUE' || e.target.value === 'HAS_VALUE') {
                                setValue(`conditions.${index}.value`, undefined);
                              } else if (numbersComparisonTypes.includes(watch(`conditions.${index}.comparisonType`))) {
                                setValue(`conditions.${index}.isCaseSensitive`, false);
                              }
                            },
                          })}
                        >
                          <option value={''}>{t('MacBotConditionForm/key12')}</option>
                          {comparisonTypes.map(item => (
                            <option key={item.key} value={item.value} className="tw-capitalize">
                              {item.key}
                            </option>
                          ))}
                        </Form.Select>
                        <Form.Control.Feedback type="invalid" className="d-block font-poppins">
                          {errors.conditions?.[index]?.comparisonType?.message}
                        </Form.Control.Feedback>
                      </Form.Group>
                      <Form.Group className="tw-flex tw-items-center tw-justify-between tw-h-12 tw-pl-4 tw-pr-3 tw-py-3 tw-bg-white tw-rounded-md tw-border tw-border-solid tw-border-gray-300 ">
                        <Form.Label htmlFor="" className="tw-text-[#11181c] tw-text-sm tw-font-normal tw-font-['Lexend'] tw-leading-tight tw-mb-0">
                          {t('MacBotConditionForm/key11')}
                        </Form.Label>

                        <Form.Switch {...register(`conditions.${index}.isCaseSensitive`)} />
                      </Form.Group>
                      {watch(`conditions.${index}.comparisonType`) &&
                        watch(`conditions.${index}.comparisonType`) !== 'HAS_NO_VALUE' &&
                        watch(`conditions.${index}.comparisonType`) !== 'HAS_VALUE' && (
                          <Form.Group className="tw-w-full tw-relative">
                            <Form.Label htmlFor="" className="tw-text-[#687076] tw-text-xs tw-font-normal tw-font-['Lexend'] tw-leading-none">
                              {t('MacBotConditionForm/key8')}
                            </Form.Label>
                            <ScenarioSimpleTextInput
                              placeholder={''}
                              setValue={text => {
                                setValue(`conditions.${index}.value`, text);
                              }}
                              value={
                                typeof getValues(`conditions.${index}.value`) === 'string'
                                  ? String(getValues(`conditions.${index}.value`))
                                  : JSON.stringify(getValues(`conditions.${index}.value`)) || ''
                              }
                              contextMenuList={contextMenuList}
                              isInvalid={!!errors.conditions?.[index]?.value}
                            />
                            <Form.Control.Feedback type="invalid" className="d-block font-poppins">
                              {errors.conditions?.[index]?.value?.message}
                            </Form.Control.Feedback>
                          </Form.Group>
                        )}
                    </div>

                    {fields.length > 1 && (
                      <div
                        role="button"
                        tabIndex={0}
                        onClick={() => remove(index)}
                        onKeyDown={e => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            remove(index);
                          }
                        }}
                        className="tw-cursor-pointer tw-self-stretch tw-mx-[10px] tw-p-2.5 tw-bg-white tw-rounded-md tw-shadow border tw-border-gray-300 tw-justify-center tw-items-center tw-gap-2 tw-inline-flex"
                      >
                        <div className="tw-flex tw-flex-col tw-gap-2 md:tw-pt-8">
                          <div className="tw-text-gray-400 tw-border-none tw-w-max tw-bg-transparent">
                            <BiSolidMinusCircle className="tw-text-red-400" size={20} />{' '}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </Col>

            <Col xs={12}>
              <PSButton
                id="btn-condition-append"
                variant="outline"
                className="tw-w-full"
                type="button"
                onClick={() =>
                  append({
                    variable: '',
                    comparisonType: '',
                    value: '',
                    isCaseSensitive: false,
                  })
                }
                icon={<BiSolidPlusCircle className="tw-text-gray-400" />}
                title={t('MacBotConditionForm/key9')}
              />
            </Col>
          </Row>

          <div className="h-[0px] border border-gray-200"></div>
        </div>
        <div className="tw-flex flex-row tw-justify-between tw-items-center">
          <div className="tw-flex tw-justify-start tw-items-center tw-gap-2">
            {<VideoScenarioDeleteButton itemData={itemData} tree={tree} deleteHandler={deleteHandler} />}

            <AddNoteButton noteFormMethods={{ getValues, setValue }} />
          </div>
          <div className="tw-flex tw-justify-end">
            <PSButton id="btn-condition-submit" type="submit" disabled={loading} isLoading={loading} title={t('MacBotResponseForm/btn-02')} />
          </div>
        </div>
        <NoteForm noteFormMethods={{ register, watch, errors, fields: noteFields }} />
      </ScenarioSide>
    </React.Fragment>
  );
};
